function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import Polyglot from 'node-polyglot';
import { SupportedLocales } from '@cloudflare/intl-types';
import markdown from '@cloudflare/util-markdown';
import mapValues from 'lodash/mapValues';
import pseudoloc from './pseudoloc';
import { TranslatorKeyNotFoundError } from './errors';
pseudoloc.option.delimiter = '%';
pseudoloc.option.startDelimiter = '{';
pseudoloc.option.endDelimiter = '}';

class Translator {
  constructor(options) {
    _defineProperty(this, "map", void 0);

    _defineProperty(this, "currentLocale", SupportedLocales.en_US);

    _defineProperty(this, "options", void 0);

    _defineProperty(this, "psudoLocalizePhrases", phrases => {
      return Object.entries(phrases).reduce((acc, [key, value]) => Object.assign(acc, {
        [key]: pseudoloc.str(value)
      }), {});
    });

    _defineProperty(this, "getInstance", locale => {
      if (locale) {
        return this.map[locale];
      } else {
        return this.map[this.currentLocale];
      }
    });

    _defineProperty(this, "getInstanceMatchingPhrase", phrase => {
      const p = this.getInstance();

      if (p.has(phrase)) {
        return p;
      } else {
        return this.getInstance(SupportedLocales.en_US);
      }
    });

    _defineProperty(this, "extend", (phrases, locale) => {
      const p = this.getInstance(locale);

      if (this.options.pseudoLoc) {
        phrases = this.psudoLocalizePhrases(phrases);
      }

      p.extend(phrases);
    });

    _defineProperty(this, "t", (phrase, arg) => {
      const p = this.getInstanceMatchingPhrase(phrase);

      if (arg) {
        if (typeof arg === 'number') {
          return p.t(phrase, arg);
        } else {
          return p.t(phrase, arg);
        }
      }

      return p.t(phrase);
    });

    _defineProperty(this, "tm", (phrase, arg) => {
      return markdown(this.t(phrase, arg));
    });

    _defineProperty(this, "clear", () => {
      this.getInstance().clear();
    });

    _defineProperty(this, "replace", phrases => {
      if (this.options.pseudoLoc) {
        phrases = this.psudoLocalizePhrases(phrases);
      }

      this.getInstance().replace(phrases);
    });

    _defineProperty(this, "locale", locale => {
      if (locale) {
        this.currentLocale = locale;

        if (!this.map[locale]) {
          this.createInstance(locale);
        }
      }

      return this.currentLocale;
    });

    _defineProperty(this, "has", phrase => {
      return this.getInstanceMatchingPhrase(phrase).has(phrase);
    });

    _defineProperty(this, "translateSeconds", seconds => {
      // Prevent 0 seconds returning "0 days"
      if (Number(seconds) !== 0) {
        if (seconds % 86400 === 0) {
          return this.t('time.num_days', {
            smart_count: seconds / 86400
          });
        }

        if (seconds % 3600 === 0) {
          return this.t('time.num_hours', {
            smart_count: seconds / 3600
          });
        }

        if (seconds % 60 === 0) {
          return this.t('time.num_minutes', {
            smart_count: seconds / 60
          });
        }
      }

      return this.t('time.num_seconds', {
        smart_count: seconds
      });
    });

    _defineProperty(this, "translateObject", phrases => {
      return mapValues(phrases, this.t);
    });

    this.map = {};
    this.options = options || {};
    this.options.allowMissing = true;

    this.options.onMissingKey = (key, options, locale) => {
      if (process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'production') {
        console.error(new TranslatorKeyNotFoundError(key)); // If a fallback is present, display that

        if (options._) return options._; // Otherwise display the raw key

        return key;
      }

      if (process.env.NODE_ENV === 'test') {
        return key;
      }

      throw new Error(`Translation error. The translate function was called on a key that does not exist or is in a catalog that has not yet been loaded. Key: ${key}. Locale: ${locale}. There are several reasons this might be happening, check the i18n FAQ for more information: https://guide.cloudflare-ui.com/docs/i18n#i18n-faq`);
    };

    this.locale(options && options.locale || SupportedLocales.en_US); //clear locale and phrases since future instances of Polyglot
    //created should be based on params passed in to functions and not
    //initial options

    if (options && options.phrases) {
      options.phrases = undefined;
    }

    if (options && options.locale) {
      options.locale = undefined;
    }
  }

  createInstance(locale) {
    if (this.options.pseudoLoc && this.options.phrases) {
      this.options.phrases = this.psudoLocalizePhrases(this.options.phrases);
    }

    this.map[locale] = new Polyglot(Object.assign(this.options, {
      locale
    }));
  }

}

export default Translator;